import { CSSProperties, useMemo, useState } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, ToggleButton, ToggleButtonGroup, Stack, Link,
    useTheme,
} from '@mui/material';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    LineChart, Line, ReferenceLine,
} from 'recharts';
import { useQuery } from 'react-query';
import { Loading } from 'react-admin';
import { supabase } from '../../lib/supabaseClient';
import { organicPalette, OrganicPalette } from './organicPalette';
import { limited, useSeen, useAsOf, daysBefore, RefreshButton, SectionLoading } from '../recapQuery';
import {
    WindowDays, OrganicSummary, FunnelStep, CoverageWeek, TrendWeek, PoolHealth, MatchedCall,
    ArmRow, EntryRow, n0, num, formatMoney, formatDelta, posthogPersonUrl, ENTRY_TYPE_LABEL,
    FUNNEL_LABEL, evaluateChecks, baselinePerWeek, armDifference, ARM_MIN_PEOPLE,
    MIN_MATCHED,
} from './organicMetrics';
import {
    SectionCard, Tag, EmptyState, Tile, ChecksTable, FunnelGroup, SplitsTable, PoolHealthBody,
} from '../tracking-pool/TrackingPoolPanels';

/**
 * Admin Analytics → Organic tab (plan docs/roadmap/ORGANIC_POOL_DNI_PLAN.md §10).
 *
 * One RPC per panel (20260923220200_organic_analytics.sql), rolling window, like
 * CasesRecap. Organic pool volumes are too thin for calendar months. Day-one
 * panels (callers, qualified, won, revenue, coverage by line, taps, splits,
 * trend) work on today's data; pool panels show "Pool not live yet (P1)" until
 * the organic pool has numbers. The only PostHog-derived figures are session
 * counts from the nightly web_traffic_daily rollup, labelled "PostHog-measured";
 * the tab itself never calls PostHog, it only links out to person timelines.
 */

const WINDOWS: { key: WindowDays; label: string }[] = [
    { key: 7, label: '7 d' },
    { key: 28, label: '28 d' },
    { key: 90, label: '90 d' },
];

// Pre-need revenue lands months later: the splits panel always uses 90 days.
const SPLITS_DAYS = 90;
// Coverage needs enough weeks to show a direction.
const COVERAGE_MIN_DAYS = 84;

const POOL_NOT_LIVE = 'Pool not live yet (P1)';

export const OrganicRecap = ({ initialWindow = 28 }: { initialWindow?: WindowDays } = {}) => {
    const theme = useTheme();
    const p = organicPalette(theme.palette.mode === 'dark' ? 'dark' : 'light');
    const [win, setWin] = useState<WindowDays>(initialWindow);

    // Everything runs up to asOf; it is in every key so Refresh refetches what is on screen.
    const [asOf, refresh] = useAsOf('organic');
    const range = useMemo(() => ({
        start: daysBefore(asOf, win),
        coverageStart: daysBefore(asOf, Math.max(win, COVERAGE_MIN_DAYS)),
        splitsStart: daysBefore(asOf, SPLITS_DAYS),
        end: asOf,
    }), [win, asOf]);

    const rpc = <T,>(fn: string, args: Record<string, unknown>) => limited(async (): Promise<T[]> => {
        const { data, error } = await supabase.rpc(fn, args);
        if (error) throw error;
        return (data ?? []) as T[];
    });
    const w = { p_start: range.start, p_end: range.end };

    // Lazy sections: a panel's RPCs run once it scrolls near the viewport (T-091). The headline
    // row (tiles, funnel, "Is it working?") always loads; its checks need suspects + trend.
    const [coverageRef, coverageSeen] = useSeen();
    const [poolRef, poolSeen] = useSeen();
    const [entryRef, entrySeen] = useSeen();

    const summary = useQuery(['organicSummary', win, asOf], rpc<OrganicSummary>('get_organic_summary', w));
    const funnel = useQuery(['organicFunnel', win, asOf], rpc<FunnelStep>('get_organic_funnel', w));
    const suspects = useQuery(['organicSuspects', win, asOf], rpc<MatchedCall>('get_organic_attribution_suspects', w));
    const trend = useQuery(['organicTrend', asOf], rpc<TrendWeek>('get_organic_weekly_trend', { p_weeks: 26 }));
    const coverage = useQuery(['organicCoverage', win, asOf],
        rpc<CoverageWeek>('get_organic_attribution_coverage', { p_start: range.coverageStart, p_end: range.end }),
        { enabled: coverageSeen });
    const pool = useQuery(['organicPool', win, asOf], limited(async (): Promise<PoolHealth | null> => {
        const { data, error } = await supabase.rpc('get_tracking_pool_health', { ...w, p_pool: 'organic' });
        if (error) throw error;
        return (data ?? null) as PoolHealth | null;
    }), { enabled: poolSeen });
    const arms = useQuery(['organicArms', win, asOf], rpc<ArmRow>('get_organic_arm_comparison', w), { enabled: poolSeen });
    const entry = useQuery(['organicEntry', win, asOf], rpc<EntryRow>('get_organic_entry_breakdown', w), { enabled: entrySeen });
    const splitArgs = (lang: string, deal: string) =>
        ({ p_start: range.splitsStart, p_end: range.end, p_language: lang, p_deal_type: deal });
    const splitOpts = { enabled: entrySeen };
    const enAt = useQuery(['organicSplit', 'en', 'at_need', asOf], rpc<FunnelStep>('get_organic_funnel', splitArgs('en', 'at_need')), splitOpts);
    const frAt = useQuery(['organicSplit', 'fr', 'at_need', asOf], rpc<FunnelStep>('get_organic_funnel', splitArgs('fr', 'at_need')), splitOpts);
    const enPre = useQuery(['organicSplit', 'en', 'pre_need', asOf], rpc<FunnelStep>('get_organic_funnel', splitArgs('en', 'pre_need')), splitOpts);
    const frPre = useQuery(['organicSplit', 'fr', 'pre_need', asOf], rpc<FunnelStep>('get_organic_funnel', splitArgs('fr', 'pre_need')), splitOpts);

    const s = summary.data?.[0];
    const live = !!s?.pool_live;

    const checks = useMemo(
        () => evaluateChecks({ summary: s, suspects: suspects.data, trend: trend.data }),
        [s, suspects.data, trend.data]);
    const baseline = useMemo(() => baselinePerWeek(trend.data), [trend.data]);

    if (summary.isLoading) return <Loading />;
    if (summary.error) {
        return (
            <Typography color="error">
                Could not load organic analytics: {String((summary.error as Error).message ?? summary.error)}
            </Typography>
        );
    }

    const tooltipStyle = {
        background: theme.palette.background.paper,
        border: `1px solid ${p.grid}`, borderRadius: 6, fontSize: 13,
    };

    return (
        <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <ToggleButtonGroup size="small" exclusive value={win} onChange={(_, v) => v && setWin(v)}>
                    {WINDOWS.map(o => <ToggleButton key={o.key} value={o.key}>Last {o.label}</ToggleButton>)}
                </ToggleButtonGroup>
                <RefreshButton asOf={asOf} onRefresh={refresh} />
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="caption" color="text.secondary">
                    Organic = every inbound call that is not on a paid line, classified by the number dialled.
                    First-time = no call from that number in the previous 180 days.
                </Typography>
            </Stack>

            {/* ---- Headline tiles ---- */}
            <Stack direction="row" sx={{ gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Tile label="Organic first-time callers" value={String(n0(s?.first_time_callers))}
                    detail={`${n0(s?.first_time_callers_answered)} answered · ${formatDelta(s?.first_time_callers, s?.first_time_callers_prev)}`} />
                <Tile label="Organic qualified" value={String(n0(s?.qualified))}
                    detail={formatDelta(s?.qualified, s?.qualified_prev)} />
                <Tile label="Organic won" value={String(n0(s?.won))}
                    detail={`contracts signed · ${formatDelta(s?.won, s?.won_prev)}`} />
                <Tile label="Organic revenue" value={formatMoney(s?.revenue_cents)}
                    detail={`paid on those contracts · ${formatDelta(s?.revenue_cents, s?.revenue_cents_prev)}`} />
                <Tile label="Attribution coverage" muted={!live}
                    value={live ? `${num(s?.coverage_pct)?.toFixed(0) ?? 0} %` : '—'}
                    detail={live ? 'of organic first-time callers tied to a visit' : POOL_NOT_LIVE} />
                <Tile label="Claims blocked (peak hour)" muted={!live}
                    value={live && num(s?.peak_hour_blocking_pct) != null ? `${num(s?.peak_hour_blocking_pct)?.toFixed(1)} %` : '—'}
                    detail={live ? `${n0(s?.claims_blocked)} of ${n0(s?.claims)} claims in the window` : POOL_NOT_LIVE} />
            </Stack>

            <Box sx={{ display: 'grid', gap: 0, gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, columnGap: 3 }}>
                <Box>
                    <FunnelPanel steps={funnel.data} p={p} />
                </Box>
                <Box>
                    <SectionCard title="Is it working?" tags={<Tag label="NEEDS P1" kind="p1" />}
                        subtitle="The P1 pass thresholds from the plan. Each stays 'not enough data yet' until the pool has run long enough to judge.">
                        {!live && (
                            <Box sx={{ mb: 1.5 }}>
                                <EmptyState title={`${POOL_NOT_LIVE}: thresholds are set, results start with the pool.`}>
                                    {baseline != null
                                        ? `Baseline for "no harm": ${baseline.toFixed(0)} organic first-time callers a week (last 8 full weeks).`
                                        : undefined}
                                </EmptyState>
                            </Box>
                        )}
                        <ChecksTable checks={checks} p={p} />
                    </SectionCard>
                </Box>
            </Box>

            {/* ---- Coverage ---- */}
            <div ref={coverageRef}>
            <SectionLoading loading={coverage.isLoading} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, columnGap: 3 }}>
                <SectionCard title="Attribution coverage — organic first-time callers by the number they dialled"
                    tags={<><Tag label="DAY ONE" kind="day1" />{!live && <Tag label="POOL SLICES NEED P1" kind="p1" />}</>}
                    subtitle={live
                        ? 'The blue share is callers tied to the page they came from. It should grow while the main-line share shrinks.'
                        : 'Today every organic caller dials the main line or the Google Business Profile number, so none can be tied to a visit.'}>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={(coverage.data ?? []).map(r => ({
                            ...r, week: r.week_start.slice(5) + (r.is_partial ? '*' : ''),
                            matched: n0(r.matched), unmatched: n0(r.unmatched), organic_default: n0(r.organic_default),
                            organic_pool_full: n0(r.organic_pool_full),
                            canonical_1770: n0(r.canonical_1770),
                            gbp: n0(r.gbp), other: n0(r.other),
                        }))} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                            <CartesianGrid vertical={false} stroke={p.grid} />
                            <XAxis dataKey="week" tick={{ fill: p.muted, fontSize: 11 }} axisLine={{ stroke: p.axis }} tickLine={false} />
                            <YAxis tick={{ fill: p.muted, fontSize: 11 }} axisLine={false} tickLine={false} width={32} />
                            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: theme.palette.action.hover }} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            {live && <Bar dataKey="matched" name="Pool — matched" stackId="c" fill={p.matched} />}
                            {live && <Bar dataKey="unmatched" name="Pool — unmatched" stackId="c" fill={p.unmatched} />}
                            {(coverage.data ?? []).some(r => n0(r.organic_default) > 0) && (
                                <Bar dataKey="organic_default" name="Organic · default number" stackId="c" fill={p.organicDefault} />
                            )}
                            {(coverage.data ?? []).some(r => n0(r.organic_pool_full) > 0) && (
                                <Bar dataKey="organic_pool_full" name="Organic · pool full" stackId="c" fill={p.organicPoolFull} />
                            )}
                            <Bar dataKey="canonical_1770" name="Main line 1770" stackId="c" fill={p.canonical} />
                            <Bar dataKey="gbp" name="GBP 805-6300" stackId="c" fill={p.gbp} />
                            <Bar dataKey="other" name="Other organic lines" stackId="c" fill={p.other} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                    <Typography variant="caption" color="text.secondary">
                        Last {Math.max(win, COVERAGE_MIN_DAYS)} days, weeks start Monday (ET); * = partial week.
                        Paid lines (pool, call extension, fallback) are excluded. Calls with no caller id are never counted as first-time.
                    </Typography>
                </SectionCard>

                <SectionCard title="Google Business Profile line" tags={<Tag label="DAY ONE" kind="day1" />}
                    subtitle={<>+1 438 805-6300, last {win} days. Labelled <b>paid</b> in call attribution today (plan Q9); counted here by the number dialled.</>}>
                    <Table size="small">
                        <TableBody>
                            <TableRow><TableCell>First-time callers</TableCell><TableCell align="right">{n0(s?.gbp_first_time_callers)}</TableCell></TableRow>
                            <TableRow><TableCell>Leads</TableCell><TableCell align="right">{n0(s?.gbp_leads)}</TableCell></TableRow>
                            <TableRow><TableCell>Qualified</TableCell><TableCell align="right">{n0(s?.gbp_qualified)}</TableCell></TableRow>
                            <TableRow><TableCell>Won</TableCell><TableCell align="right">{n0(s?.gbp_won)}</TableCell></TableRow>
                            <TableRow><TableCell>Revenue</TableCell><TableCell align="right">{formatMoney(s?.gbp_revenue_cents)}</TableCell></TableRow>
                        </TableBody>
                    </Table>
                    <Typography variant="caption" color="text.secondary">
                        Not included in the organic tiles above, so they don&apos;t jump when the GBP relabel ships.
                    </Typography>
                </SectionCard>
            </Box>

            </div>

            {/* ---- Pool health ---- */}
            <div ref={poolRef}>
            <SectionLoading loading={pool.isLoading || arms.isLoading} />
            <PoolPanel pool={pool.data ?? null} live={live} p={p} tooltipStyle={tooltipStyle} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, columnGap: 3 }}>
                <ArmPanel arms={arms.data} live={live} />
                <MatchedCallsPanel calls={suspects.data} live={live} matched={n0(s?.pool_matched_calls)} />
            </Box>
            </div>

            <div ref={entryRef}>
            <SectionLoading loading={entry.isLoading || enAt.isLoading || frAt.isLoading || enPre.isLoading || frPre.isLoading} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, columnGap: 3 }}>
                <EntryPanel rows={entry.data} live={live} win={win} />
                <SectionCard title={`FR / EN × at-need / pre-need · last ${SPLITS_DAYS} days`} tags={<Tag label="DAY ONE" kind="day1" />}
                    subtitle="All organic leads (not yet tied to a visit). Pre-need revenue lands 6–12 months later, so don't read it against at-need on this window.">
                    <SplitsTable rows={[
                        ['EN · at-need', enAt.data], ['FR · at-need', frAt.data],
                        ['EN · pre-need', enPre.data], ['FR · pre-need', frPre.data],
                    ]} />
                </SectionCard>
            </Box>
            </div>

            <SectionCard title="Weekly trend · 26 weeks" tags={<Tag label="DAY ONE" kind="day1" />}
                subtitle={`Organic first-time callers and qualified leads${live ? ', plus pool-matched calls' : ''}. The last week is partial.`}>
                <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={(trend.data ?? []).map(r => ({
                        week: r.week_start.slice(5) + (r.is_partial ? '*' : ''),
                        callers: n0(r.first_time_callers), qualified: n0(r.qualified), matched: n0(r.pool_matched),
                    }))} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                        <CartesianGrid vertical={false} stroke={p.grid} />
                        <XAxis dataKey="week" tick={{ fill: p.muted, fontSize: 11 }} axisLine={{ stroke: p.axis }} tickLine={false} interval={3} />
                        <YAxis tick={{ fill: p.muted, fontSize: 11 }} axisLine={false} tickLine={false} width={32} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {baseline != null && <ReferenceLine y={baseline} stroke={p.axis} strokeDasharray="4 3" />}
                        <Line type="monotone" dataKey="callers" name="First-time callers" stroke={p.callers} strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="qualified" name="Qualified" stroke={p.qualified} strokeWidth={2} dot={false} />
                        {live && <Line type="monotone" dataKey="matched" name="Pool-matched calls" stroke={p.matched} strokeDasharray="5 3" strokeWidth={2} dot={false} />}
                    </LineChart>
                </ResponsiveContainer>
                {baseline != null && (
                    <Typography variant="caption" color="text.secondary">
                        Dashed line: 8-week baseline, {baseline.toFixed(0)} first-time callers a week.
                    </Typography>
                )}
            </SectionCard>
        </Box>
    );
};

// ───────────────────────────────────────────────────────────────────────────────

const FunnelPanel = ({ steps, p }: { steps: FunnelStep[] | undefined; p: OrganicPalette }) => {
    const rows = (steps ?? []).slice().sort((a, b) => a.step_order - b.step_order);
    const web = rows.filter(r => r.step_order <= 5);
    const phone = rows.filter(r => r.step_order >= 6);
    const revenue = rows.find(r => r.step === 'revenue');
    const group = (title: string, list: FunnelStep[]) => (
        <FunnelGroup title={title} list={list} labels={FUNNEL_LABEL} color={p.matched}
            unavailableText={step => (step === 'sessions' ? 'Nightly PostHog rollup not running yet' : POOL_NOT_LIVE)} />
    );
    return (
        <SectionCard title="Organic funnel" tags={<><Tag label="DAY ONE: LEAD → REVENUE" kind="day1" /><Tag label="SESSIONS: PostHog-measured" kind="posthog" /></>}
            subtitle="Two scales on purpose: website steps are thousands, phone steps tens. Each group is scaled on its own. Numbers in brackets are tied to a pool assignment.">
            {group('On the website', web)}
            {group('On the phone', phone)}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Revenue: {formatMoney(revenue?.value_cents)} from {n0(revenue?.n)} paid organic contracts signed in the window
            </Typography>
            <Typography variant="caption" color="text.secondary">
                Period counts, not a cohort: each step counts what happened in the window. Sessions come from PostHog and undercount ad-blocker users.
            </Typography>
        </SectionCard>
    );
};

const PoolPanel = ({ pool, live, p, tooltipStyle }: {
    pool: PoolHealth | null; live: boolean; p: OrganicPalette; tooltipStyle: CSSProperties;
}) => (
    <SectionCard title="Pool health — numbers in use by hour (ET)" tags={<Tag label="NEEDS P1" kind="p1" />}
        subtitle={live ? 'Mean and max numbers held per hour of the day vs the active pool size. Any hour reaching the red line blocked claims.' : undefined}>
        {!live || !pool ? (
            <EmptyState title={POOL_NOT_LIVE}>
                After the first numbers are active this shows claims and blocked claims by hour, numbers in use vs
                pool size, numbers in quarantine / active / retired, junk calls, and the delay from assignment to call
                (p50 / p90) against the 30 + 90 minute window.
            </EmptyState>
        ) : (
            <PoolHealthBody pool={pool} p={p} tooltipStyle={tooltipStyle} />
        )}
    </SectionCard>
);

const ArmPanel = ({ arms, live }: { arms: ArmRow[] | undefined; live: boolean }) => {
    const d = armDifference(arms);
    return (
        <SectionCard title="Toll-free vs local (A/B)" tags={<Tag label="NEEDS P1" kind="p1" />}
            subtitle="Each eligible person is put in one arm and stays there. Calls per 100 people, difference with a 90 % interval.">
            {!live ? (
                <EmptyState title={POOL_NOT_LIVE}>
                    Both arms get a tracked pool number (toll-free vs local 438), so both can be measured. About
                    {` ${ARM_MIN_PEOPLE.toLocaleString('en-CA')}`} people per arm (~10 weeks) are needed to see a 50 % difference in call rate.
                </EmptyState>
            ) : (
                <>
                    <Table size="small">
                        <TableHead><TableRow>
                            <TableCell>Arm</TableCell><TableCell align="right">People</TableCell><TableCell align="right">Calls</TableCell>
                            <TableCell align="right">Per 100</TableCell><TableCell align="right">Qualified</TableCell><TableCell align="right">Won</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                            {(arms ?? []).map(a => (
                                <TableRow key={a.arm}>
                                    <TableCell>{a.arm === 'toll_free' ? 'Toll-free' : 'Local 438'}</TableCell>
                                    <TableCell align="right">{n0(a.eligible_people)}</TableCell>
                                    <TableCell align="right">{n0(a.calls)}</TableCell>
                                    <TableCell align="right">{num(a.calls_per_100)?.toFixed(2) ?? '—'}</TableCell>
                                    <TableCell align="right">{n0(a.qualified)}</TableCell>
                                    <TableCell align="right">{n0(a.won)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        {d == null
                            ? 'Not enough data yet: no eligible people in one of the arms.'
                            : d.enough
                                ? `Toll-free − local: ${d.diff.toFixed(2)} calls per 100 (90 % interval ${d.lo.toFixed(2)} to ${d.hi.toFixed(2)}).`
                                : `Not enough data yet: ${Math.min(...(arms ?? []).map(a => n0(a.eligible_people))).toLocaleString('en-CA')} of ~${ARM_MIN_PEOPLE.toLocaleString('en-CA')} people per arm. Current gap ${d.diff.toFixed(2)} per 100 (${d.lo.toFixed(2)} to ${d.hi.toFixed(2)}).`}
                    </Typography>
                </>
            )}
        </SectionCard>
    );
};

const MatchedCallsPanel = ({ calls, live, matched }: { calls: MatchedCall[] | undefined; live: boolean; matched: number }) => {
    const list = calls ?? [];
    const sus = list.filter(c => c.is_suspect);
    return (
        <SectionCard title="Cross-attribution suspects & matched calls" tags={<Tag label="NEEDS P1" kind="p1" />}
            subtitle="Matched calls where the page language differs from the lead's, the caller is outside Quebec/Ontario, or more than one visit could match. Should be near zero by design. Each row links to the lead and to the caller's PostHog person timeline.">
            {!live || list.length === 0 ? (
                <EmptyState title={live ? 'No matched calls in this window.' : POOL_NOT_LIVE}>
                    {live ? undefined : 'Every matched call will be listed here with its entry page and a PostHog person link.'}
                </EmptyState>
            ) : (
                <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {sus.length} of {matched} matched call{matched === 1 ? '' : 's'} flagged
                        {matched < MIN_MATCHED ? ` — not enough data yet to judge (needs ${MIN_MATCHED})` : ''}.
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                        <Table size="small" stickyHeader>
                            <TableHead><TableRow>
                                <TableCell>Call</TableCell><TableCell>Page (lang)</TableCell><TableCell>Lead lang</TableCell>
                                <TableCell>Flag</TableCell><TableCell>Links</TableCell>
                            </TableRow></TableHead>
                            <TableBody>
                                {list.slice(0, 50).map(c => {
                                    const ph = posthogPersonUrl(c.posthog_distinct_id);
                                    return (
                                        <TableRow key={c.call_session_id} hover>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(c.called_at).toLocaleString('en-CA', { dateStyle: 'short', timeStyle: 'short' })}</TableCell>
                                            <TableCell>{c.trigger_page ?? '—'} ({c.assignment_language ?? '?'})</TableCell>
                                            <TableCell>{c.lead_language ?? '—'}</TableCell>
                                            <TableCell>{c.reasons.length ? c.reasons.join(', ').replace(/_/g, ' ') : 'clean'}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {c.lead_id && <Link href={`#/leads/${c.lead_id}/show`} sx={{ mr: 1 }}>Lead</Link>}
                                                {ph && <Link href={ph} target="_blank" rel="noopener noreferrer">PostHog</Link>}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </SectionCard>
    );
};

const EntryPanel = ({ rows, live, win }: { rows: EntryRow[] | undefined; live: boolean; win: number }) => {
    const types = (rows ?? []).filter(r => r.grain === 'type');
    const pages = (rows ?? []).filter(r => r.grain === 'page' && n0(r.phone_taps) + n0(r.assignments) > 0);
    const hasSessions = types.some(r => r.sessions != null);
    const maxTaps = Math.max(1, ...types.map(r => n0(r.phone_taps)));
    const dash = <span style={{ opacity: 0.5 }}>—</span>;
    return (
        <SectionCard title={`Entry page · last ${win} days`}
            tags={<><Tag label="TAPS: DAY ONE" kind="day1" /><Tag label="CALLS ONWARD: NEEDS P1" kind="p1" /></>}
            subtitle={`Phone taps (first-party, since 2026-08-26) by the page the visit entered on${hasSessions ? '; sessions are PostHog-measured' : ''}. Obituary pages are rolled up, never listed by name.`}>
            <TableContainer>
                <Table size="small">
                    <TableHead><TableRow>
                        <TableCell>Entry page type</TableCell>
                        <TableCell align="right">Sessions</TableCell>
                        <TableCell align="right">Taps</TableCell>
                        <TableCell align="right">Calls</TableCell>
                        <TableCell align="right">Qual.</TableCell>
                        <TableCell align="right">Won</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                        {types.map(r => (
                            <TableRow key={r.entry_page_type}>
                                <TableCell>
                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                        <Box sx={{ width: Math.max(2, (60 * n0(r.phone_taps)) / maxTaps), height: 10, bgcolor: 'primary.main', borderRadius: '0 4px 4px 0', opacity: 0.8 }} />
                                        <span>{ENTRY_TYPE_LABEL[r.entry_page_type] ?? r.entry_page_type}</span>
                                    </Stack>
                                </TableCell>
                                <TableCell align="right">{r.sessions == null ? dash : n0(r.sessions).toLocaleString('en-CA')}</TableCell>
                                <TableCell align="right">{n0(r.phone_taps)}</TableCell>
                                <TableCell align="right">{live ? n0(r.calls) : dash}</TableCell>
                                <TableCell align="right">{live ? n0(r.qualified) : dash}</TableCell>
                                <TableCell align="right">{live ? n0(r.won) : dash}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {!hasSessions && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Sessions appear once the nightly PostHog rollup (web_traffic_daily) runs.
                </Typography>
            )}
            {pages.length > 0 && (
                <>
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Top pages</Typography>
                    <Table size="small">
                        <TableHead><TableRow>
                            <TableCell>Page</TableCell><TableCell align="right">Taps</TableCell>
                            <TableCell align="right">People</TableCell><TableCell align="right">Numbers shown</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                            {pages.slice(0, 10).map(r => (
                                <TableRow key={r.page ?? ''}>
                                    <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.page ?? ''}>{r.page}</TableCell>
                                    <TableCell align="right">{n0(r.phone_taps)}</TableCell>
                                    <TableCell align="right">{n0(r.tap_people)}</TableCell>
                                    <TableCell align="right">{n0(r.assignments)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}
        </SectionCard>
    );
};
